import moment from "moment";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import { ReactNode, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/layout/Layout";
import SessionContext from "../components/layout/SessionContext";
import useRefresh from "../components/Refresh/refresh";
import DeletionModal from "../components/ui/DeletionModal";
import FilterEvents, { EventType, eventTypes } from "../components/WeekAtAGlance/FilterEvents";
import ClubPreviewCard from "../components/WeekAtAGlance/ClubPreviewCard";
import EventCreationBanner from "../components/WeekAtAGlance/EventCreationBanner";
import EmailBanner from "../components/membership/EmailBanner";
import UserWaitlist from "../components/membership/UserWaitlist";
import { ClubMode, ClubOption } from "../components/clubs/clubApi";
import ClubAccessBanners from "../components/clubs/ClubAccessBanners";
import useClubAccess from "../components/clubs/useClubAccess";
import useClubActions from "../components/clubs/useClubActions";
import useUserClubs from "../components/clubs/useUserClubs";
import useClubLogos from "../components/clubs/useClubLogos";
import { ClubLogo } from "../components/clubs/ClubLogo";
import AllClubsSchedule from "../components/WeekAtAGlance/AllClubsSchedule";

type Setter = (value: any) => void;

export type HomePageProps = {
  mode: ClubMode;
  devPort: string;
  userName: string;
  clubId: number;
  setClubId: Setter;
  setPlayerBoard?: Setter;
  betaMode?: boolean;
  waiverNotifications?: number;
  setSubscriptionMode?: Setter;
  setEventId?: Setter;
  setLessonPro?: Setter;
  setLessonPrice?: Setter;
  setLeasePrice?: Setter;
  setGameDate?: Setter;
  setGamePoloType?: Setter;
  setGameDivisions?: Setter;
  setEventDetails?: Setter;
  setPricingTab?: Setter;
};

function useScheduleFilters() {
  const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment().add(6, "months").format("YYYY-MM-DD"));
  const [showOnlyBookedLessons, setShowOnlyBookedLessons] = useState(false);
  const [selectedEventTypes, setSelectedEventTypes] = useState<EventType[]>(eventTypes);
  return {
    startDate,
    endDate,
    showOnlyBookedLessons,
    selectedEventTypes,
    filterProps: {
      startDate,
      setStartDate,
      endDate,
      setEndDate,
      showOnlyBookedLessons,
      setShowOnlyBookedLessons,
      selectedEventTypes,
      setSelectedEventTypes,
      filterGames: () => {},
    },
  };
}

function PageHeader({
  title,
  subtitle,
  logo,
}: {
  title: string;
  subtitle: string;
  logo?: ReactNode;
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      {logo}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }} data-testid="home-title">
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {subtitle}
        </Typography>
      </Box>
    </Stack>
  );
}

function ClubHeaderLogo({ devPort, club }: { devPort: string; club: ClubOption }) {
  const logos = useClubLogos(devPort, [club.value]);
  return <ClubLogo clubId={club.value} name={club.label} logo={logos[club.value]} size={48} />;
}

function HomeShell({
  layoutMode,
  props,
  loading,
  children,
}: {
  layoutMode: number;
  props: HomePageProps;
  loading: boolean;
  children: ReactNode;
}) {
  return (
    <Layout
      managerMode={layoutMode}
      waiverNotifications={props.waiverNotifications}
      betaMode={props.betaMode}
      devPort={props.devPort}
    >
      <Card>
        {loading && <LinearProgress color="secondary" />}
        <CardContent>{!loading && <Stack spacing={3}>{children}</Stack>}</CardContent>
      </Card>
    </Layout>
  );
}

function eventCardProps(props: HomePageProps) {
  return {
    devPort: props.devPort,
    setLessonPro: props.setLessonPro,
    setLessonPrice: props.setLessonPrice,
    setEventId: props.setEventId,
    setClubId: props.setClubId,
    setLeasePrice: props.setLeasePrice,
    setGameDate: props.setGameDate,
    setGamePoloType: props.setGamePoloType,
    setGameDivisions: props.setGameDivisions,
    setEventDetails: props.setEventDetails,
  };
}

function ManagerHome({ props, club }: { props: HomePageProps; club: ClubOption }) {
  const navigate = useNavigate();
  const { devPort, userName, betaMode = false } = props;
  const access = useClubAccess(devPort, club.value, true);
  const schedule = useScheduleFilters();

  const [createModal, setCreateModal] = useState(false);
  const [registerModal, setRegisterModal] = useState(false);
  const [updateModal, setUpdateModal] = useState(false);
  const [updateLessonModal, setUpdateLessonModal] = useState(false);
  const [displayTournamentModal, setDisplayTournamentModal] = useState(false);
  const [eventToEdit, setEventToEdit] = useState<any>();
  const [gameToDelete, setGameToDelete] = useState<number>();
  const [deleteModal, setDeleteModal] = useState(false);
  const [lessonToDeleteId, setLessonToDeleteId] = useState<any>();
  const [lessonDeleteMessage, setLessonDeleteMessage] = useState<string>();
  const [deleteModalLesson, setDeleteModalLesson] = useState(false);
  const [alertMessage, setAlertMessage] = useState(false);
  const [messageContent, setMessageContent] = useState("added to");
  const [reload, setReload] = useState(false);

  useEffect(() => {
    if (lessonToDeleteId) {
      setDeleteModalLesson(true);
    }
  }, [lessonToDeleteId]);

  function deleteGame(gameId?: number) {
    fetch(devPort + "delete_game/" + gameId, { method: "POST", credentials: "include" }).then(
      () => {
        setDeleteModal(false);
        setMessageContent("deleted from");
        setAlertMessage(true);
        setReload((value) => !value);
      }
    );
  }

  function deleteLesson(lessonId: any) {
    fetch(devPort + "delete_lesson/" + lessonId, { method: "POST", credentials: "include" }).then(
      () => {
        setDeleteModalLesson(false);
        setReload((value) => !value);
      }
    );
  }

  return (
    <>
      <PageHeader
        title={club.label}
        subtitle="Upcoming games, lessons and tournaments"
        logo={<ClubHeaderLogo devPort={devPort} club={club} />}
      />
      <EmailBanner devPort={devPort} userName={userName} managerMode={-1} />
      <ClubAccessBanners
        access={access}
        clubId={club.value}
        clubName={club.label}
        devPort={devPort}
        userName={userName}
        setSubscriptionMode={props.setSubscriptionMode}
      />
      {access.loading && <LinearProgress color="secondary" />}
      {access.hasAccess && (
        <>
          <Snackbar open={alertMessage} autoHideDuration={3000} onClose={() => setAlertMessage(false)}>
            <Alert severity="success">Game has been {messageContent} the schedule!</Alert>
          </Snackbar>
          <DeletionModal
            deleteModal={deleteModal}
            setDeleteModal={setDeleteModal}
            deleteString="Game"
            deleteId={gameToDelete}
            setDeleteId={setGameToDelete}
            deleteMessage={undefined}
            onDelete={() => deleteGame(gameToDelete)}
          />
          <DeletionModal
            deleteModal={deleteModalLesson}
            setDeleteModal={setDeleteModalLesson}
            deleteString="Lesson"
            deleteId={lessonToDeleteId}
            setDeleteId={setLessonToDeleteId}
            deleteMessage={lessonDeleteMessage}
            onDelete={() => deleteLesson(lessonToDeleteId)}
          />
          <Paper elevation={2} sx={{ p: 2 }}>
            <Stack spacing={2}>
              <EventCreationBanner
                betaMode={betaMode}
                createModal={createModal}
                setCreateModal={setCreateModal}
                setRegisterModal={setRegisterModal}
                devPort={devPort}
                clubId={club.value}
                setAlertMessage={(value) => setAlertMessage(!!value)}
                setMessageContent={setMessageContent}
                filterGames={() => setReload((value) => !value)}
                setGameToDelete={setGameToDelete}
                setDeleteModal={setDeleteModal}
                updateModal={updateModal}
                setUpdateModal={setUpdateModal}
                registerModal={registerModal}
                setPricingTab={props.setPricingTab ?? (() => {})}
                eventToEdit={eventToEdit}
                setEventToEdit={setEventToEdit}
                displayTournamentModal={displayTournamentModal}
                setDisplayTournamentModal={setDisplayTournamentModal}
                updateLessonModal={updateLessonModal}
                setUpdateLessonModal={setUpdateLessonModal}
                setLessonToDeleteId={setLessonToDeleteId}
                setLessonDeleteMessage={setLessonDeleteMessage}
              />
              <FilterEvents {...schedule.filterProps} />
              <Button
                variant="outlined"
                color="primary"
                startIcon={<CalendarMonthIcon />}
                onClick={() => navigate("/tournamentcalendar")}
              >
                Tournament Calendar
              </Button>
            </Stack>
          </Paper>
          <ClubPreviewCard
            {...eventCardProps(props)}
            club={{ label: "Upcoming Events", value: club.value }}
            managerMode={-1}
            weekAtAGlance={false}
            setGameDelete={undefined}
            setGameToDelete={setGameToDelete}
            setEventToEdit={setEventToEdit}
            setUpdateModal={setUpdateModal}
            setUpdateLessonModal={setUpdateLessonModal}
            setDisplayTournamentModal={setDisplayTournamentModal}
            selectedEventTypes={schedule.selectedEventTypes}
            startDate={schedule.startDate}
            endDate={schedule.endDate}
            showOnlyBookedLessons={schedule.showOnlyBookedLessons}
            setLessonDeleteMessage={setLessonDeleteMessage}
            setLessonToDeleteId={setLessonToDeleteId}
            reload={reload}
          />
        </>
      )}
      <UserWaitlist devPort={devPort} userList={[club]} managerMode={-1} />
    </>
  );
}

function PlayerHome({
  props,
  playerId,
  clubs,
  waitlistClubs,
}: {
  props: HomePageProps;
  playerId: number;
  clubs: ClubOption[];
  waitlistClubs: ClubOption[];
}) {
  const { devPort, userName, clubId } = props;
  const schedule = useScheduleFilters();
  const { openJoin, dialogs } = useClubActions("player", devPort);
  const selectedClub = clubs.find((club) => club.value === clubId);
  const singleClub = selectedClub ?? (clubs.length === 1 ? clubs[0] : undefined);

  return (
    <>
      <PageHeader
        title={selectedClub ? selectedClub.label : "My schedule"}
        subtitle={
          selectedClub
            ? "Upcoming games, lessons and tournaments"
            : "Upcoming games, lessons and tournaments at all your clubs"
        }
        logo={selectedClub && <ClubHeaderLogo devPort={devPort} club={selectedClub} />}
      />
      <EmailBanner devPort={devPort} userName={userName} managerMode={playerId} />
      {clubs.length === 0 && waitlistClubs.length > 0 && (
        <Alert severity="info">
          Your membership request is waiting for the club manager's approval. Events will show
          up here once you're approved.
        </Alert>
      )}
      {clubs.length > 0 && <FilterEvents {...schedule.filterProps} />}
      {!singleClub && clubs.length > 0 && (
        <AllClubsSchedule
          {...eventCardProps(props)}
          clubs={clubs}
          playerId={playerId}
          startDate={schedule.startDate}
          endDate={schedule.endDate}
          selectedEventTypes={schedule.selectedEventTypes}
          showOnlyBookedLessons={schedule.showOnlyBookedLessons}
        />
      )}
      {singleClub && (
        <ClubPreviewCard
          key={singleClub.value}
          {...eventCardProps(props)}
          club={singleClub}
          managerMode={playerId}
          weekAtAGlance={false}
          setGameDelete={undefined}
          setGameToDelete={undefined}
          setEventToEdit={undefined}
          setUpdateModal={undefined}
          setUpdateLessonModal={undefined}
          setDisplayTournamentModal={undefined}
          selectedEventTypes={schedule.selectedEventTypes}
          startDate={schedule.startDate}
          endDate={schedule.endDate}
          showOnlyBookedLessons={schedule.showOnlyBookedLessons}
          setLessonDeleteMessage={undefined}
          setLessonToDeleteId={undefined}
          reload={undefined}
        />
      )}
      <UserWaitlist devPort={devPort} userList={waitlistClubs} managerMode={playerId} />
      <Button
        variant="outlined"
        color="secondary"
        startIcon={<GroupAddIcon />}
        onClick={openJoin}
        data-testid="join-club-button"
      >
        Join another club
      </Button>
      {dialogs}
    </>
  );
}

function HomePage(props: HomePageProps) {
  const { mode, devPort, userName, clubId, setPlayerBoard } = props;
  const navigate = useNavigate();
  const { setClubId: setSessionClubId } = useContext(SessionContext);
  const { state } = useUserClubs(devPort, userName, mode);
  useRefresh(userName, devPort, true);

  useEffect(() => {
    if (state.status === "noAccount") {
      navigate("/selectmembership");
    } else if (
      state.status === "ready" &&
      state.clubs.length === 0 &&
      state.waitlistClubs.length === 0
    ) {
      navigate(mode === "manager" ? "/onboarding/manager" : "/onboarding/player");
    }
  }, [state, mode, navigate]);

  useEffect(() => {
    if (state.status === "ready" && setPlayerBoard) {
      setPlayerBoard(mode === "player" ? state.userId : -1);
    }
  }, [state, mode, setPlayerBoard]);

  const ready = state.status === "ready" ? state : null;

  if (mode === "manager") {
    const club =
      ready?.clubs.find((option) => option.value === clubId) ?? ready?.clubs[0] ?? null;
    return (
      <HomeShell layoutMode={-1} props={props} loading={!club}>
        {club && <ManagerHome key={club.value} props={{ ...props, clubId: club.value }} club={club} />}
      </HomeShell>
    );
  }

  return (
    <HomeShell layoutMode={ready ? ready.userId : -2} props={props} loading={!ready}>
      {ready && (
        <PlayerHome
          props={{ ...props, setClubId: props.setClubId ?? setSessionClubId }}
          playerId={ready.userId}
          clubs={ready.clubs}
          waitlistClubs={ready.waitlistClubs}
        />
      )}
    </HomeShell>
  );
}

export default HomePage;
